import { useCallback, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
    orderBy, OrderByDirection, query, where,
} from 'firebase/firestore';
import { ViewContextType } from 'pages/Opportunities/types';
import useCollectionWithPagination from 'data/firestore/agent/opportunity/useCollectionWithPagination';
import useSearchOpportunities from 'data/hooks/useSearchOpportunities';
import { OpportunitiesSortBy } from 'data/hooks/useSearchOpportunities/types';
import { SortOrder } from 'types';
import { PAGE_SIZE } from '../../../../../constants';
import { DEFAULT_SORT_OPPORTUNITIES } from '../../contants';

const useListOpportunities = (agentId: string) => {
    const [activePage, setActivePage] = useState(1);
    const {
        selectedPipeline = '',
        stages = [],
        sortBy: sort = DEFAULT_SORT_OPPORTUNITIES,
        filters,
        activeFiltersCount = 0,
        searchQuery,
    } = useOutletContext<ViewContextType>();

    const {
        data: opportunitites, isLoading: isLoadingOpportunities, nextPage, prevPage, totalRecords,
    } = useCollectionWithPagination(agentId as string, {
        limit: PAGE_SIZE,
        listQueryFn: (ref) => query(
            ref,
            where('pipelineId', '==', selectedPipeline),
            where('removed', '==', false),
            orderBy(sort.sortBy, sort.order as OrderByDirection),
        ),
        disabled: !agentId || !selectedPipeline || !stages.length,
        track: [agentId, selectedPipeline, sort.order, sort.sortBy],
    });

    const searchEnabled = activeFiltersCount > 0 || Boolean(searchQuery);

    const { data: searcResults, isLoading: isSearching } = useSearchOpportunities({
        agentId,
        searchQuery,
        createdAt: filters.createdAt as string,
        pipelineId: selectedPipeline as string,
        stageIds: filters.stage as string,
        tags: filters.tags as string,
        sortBy: sort.sortBy as OpportunitiesSortBy,
        sortOrder: sort.order as SortOrder,
        score: filters.score as string,
        owner: filters.owner as string,
        amount: filters.amount as string,
        pageSize: PAGE_SIZE,
        page: activePage,
        queryOptions: {
            enabled: searchEnabled,
        },
    });

    const isLoading = isLoadingOpportunities || isSearching;

    const changePage = useCallback((event: React.ChangeEvent<unknown>, page: number) => {
        if (isLoading) return;
        if (page === activePage) return;
        if (!searchEnabled) {
            if (page > activePage) nextPage();
            if (page < activePage) prevPage();
        }
        setActivePage(page);
    }, [activePage, searchEnabled, isLoading, nextPage, prevPage]);

    const dataToShow = (searchEnabled ? searcResults?.hits : opportunitites) || [];
    const totalHits = searchEnabled && typeof searcResults?.pagination.total === 'number'
        ? searcResults.pagination.total
        : totalRecords;
    const pages = Math.ceil(totalHits / PAGE_SIZE);
    const firstItemOfActivePage = dataToShow.length === 0 ? 0 : PAGE_SIZE * (activePage - 1) + 1;
    const lastItemOfActivePage = Math.min(
        PAGE_SIZE * activePage,
        (PAGE_SIZE * activePage - PAGE_SIZE) + dataToShow.length,
    );

    return {
        isLoading,
        opportunities: dataToShow,
        firstItemOfActivePage,
        lastItemOfActivePage,
        totalHits,
        pages,
        activePage,
        changePage,
    };
};

export default useListOpportunities;
